import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../../../environments/environment';
import {
  WorkflowGraphResponse,
  WorkflowNodeRequest,
  WorkflowNodeResponse,
  WorkflowNodePositionRequest,
  WorkflowNodeConfigRequest,
  WorkflowEdgeRequest,
  WorkflowEdgeResponse,
  WorkflowGraphSaveRequest,
  WorkflowValidationResponse,
  WorkflowNodeCatalogResponse,
  WorkflowDuplicateNodeRequest,
  WorkflowDuplicateSectionRequest,
  WorkflowListViewResponse
} from '../models/workflow-builder.model';

@Injectable({ providedIn: 'root' })
export class WorkflowBuilderApiService {
  private readonly apiUrl = `${environment.apiUrl}api/workflows`;

  constructor(private http: HttpClient) {}

  getGraph(workflowId: number, versionId?: number): Observable<WorkflowGraphResponse> {
    let params = new HttpParams();
    if (versionId) params = params.set('versionId', versionId.toString());
    return this.http.get<WorkflowGraphResponse>(`${this.apiUrl}/${workflowId}/builder/graph`, { params });
  }

  createNode(workflowId: number, request: WorkflowNodeRequest): Observable<WorkflowNodeResponse> {
    return this.http.post<WorkflowNodeResponse>(`${this.apiUrl}/${workflowId}/builder/nodes`, request);
  }

  updateNode(workflowId: number, nodeId: number, request: WorkflowNodeRequest): Observable<WorkflowNodeResponse> {
    return this.http.put<WorkflowNodeResponse>(`${this.apiUrl}/${workflowId}/builder/nodes/${nodeId}`, request);
  }

  updateNodePosition(workflowId: number, nodeId: number, request: WorkflowNodePositionRequest): Observable<WorkflowNodeResponse> {
    return this.http.patch<WorkflowNodeResponse>(`${this.apiUrl}/${workflowId}/builder/nodes/${nodeId}/position`, request);
  }

  updateNodeConfig(workflowId: number, nodeId: number, request: WorkflowNodeConfigRequest): Observable<WorkflowNodeResponse> {
    return this.http.patch<WorkflowNodeResponse>(`${this.apiUrl}/${workflowId}/builder/nodes/${nodeId}/config`, request);
  }

  deleteNode(workflowId: number, nodeId: number, workflowVersionId: number): Observable<void> {
    const params = new HttpParams().set('workflowVersionId', workflowVersionId.toString());
    return this.http.delete<void>(`${this.apiUrl}/${workflowId}/builder/nodes/${nodeId}`, { params });
  }

  createEdge(workflowId: number, request: WorkflowEdgeRequest): Observable<WorkflowEdgeResponse> {
    return this.http.post<WorkflowEdgeResponse>(`${this.apiUrl}/${workflowId}/builder/edges`, request);
  }

  updateEdge(workflowId: number, edgeId: number, request: WorkflowEdgeRequest): Observable<WorkflowEdgeResponse> {
    return this.http.put<WorkflowEdgeResponse>(`${this.apiUrl}/${workflowId}/builder/edges/${edgeId}`, request);
  }

  deleteEdge(workflowId: number, edgeId: number, workflowVersionId: number): Observable<void> {
    const params = new HttpParams().set('workflowVersionId', workflowVersionId.toString());
    return this.http.delete<void>(`${this.apiUrl}/${workflowId}/builder/edges/${edgeId}`, { params });
  }

  saveGraph(workflowId: number, request: WorkflowGraphSaveRequest): Observable<WorkflowGraphResponse> {
    return this.http.put<WorkflowGraphResponse>(`${this.apiUrl}/${workflowId}/builder/graph`, request);
  }

  validate(workflowId: number, versionId: number): Observable<WorkflowValidationResponse> {
    const params = new HttpParams().set('versionId', versionId.toString());
    return this.http.post<WorkflowValidationResponse>(`${this.apiUrl}/${workflowId}/builder/validate`, {}, { params });
  }

  duplicateNode(workflowId: number, nodeId: number, request: WorkflowDuplicateNodeRequest): Observable<WorkflowNodeResponse> {
    return this.http.post<WorkflowNodeResponse>(`${this.apiUrl}/${workflowId}/builder/nodes/${nodeId}/duplicate`, request);
  }

  duplicateSection(workflowId: number, request: WorkflowDuplicateSectionRequest): Observable<WorkflowGraphResponse> {
    return this.http.post<WorkflowGraphResponse>(`${this.apiUrl}/${workflowId}/builder/sections/duplicate`, request);
  }

  getCatalog(workflowId: number, searchTerm?: string, nodeType?: string, objectType?: string): Observable<WorkflowNodeCatalogResponse> {
    let params = new HttpParams();
    if (searchTerm)  params = params.set('searchTerm', searchTerm);
    if (nodeType)    params = params.set('nodeType', nodeType);
    if (objectType)  params = params.set('objectType', objectType);
    return this.http.get<WorkflowNodeCatalogResponse>(`${this.apiUrl}/${workflowId}/builder/catalog`, { params });
  }

  getListView(workflowId: number, versionId?: number): Observable<WorkflowListViewResponse> {
    let params = new HttpParams();
    if (versionId) params = params.set('versionId', versionId.toString());
    return this.http.get<WorkflowListViewResponse>(`${this.apiUrl}/${workflowId}/builder/list-view`, { params });
  }
}
